import { DrawSystemImpl, System } from "minecs";
import type { ReadOnlyGameModel } from "yage/game/GameModel";
import { Container, Graphics, Text } from "pixi.js";
import { Transform } from "yage/schemas/entity/Transform";
import { AirportState, AirportAgent, AirportCash } from "../components/index.js";
import { state, ids, stations } from "../services/world.js";
import { LOCAL } from "../services/local.js";
import { AirportScene } from "./Scene.js";
import { C, label } from "../render/primitives.js";
import { projectWorld } from '../render/camera.js';
import { collectedAmount } from '../render/feel.js';
import { stationPad, cashPoint, CONFIG } from '../domain/config.js';
import { missionsFor } from '../domain/missions.js';
import { AirportPlane } from '../components/index.js';
const CONFETTI = [C.gold, C.teal, 0xce9160, 0xa677c5, 0x8fa6e6];
@System(AirportState)
export class AirportEffects extends DrawSystemImpl<ReadOnlyGameModel> {
    static depth = 11600;
    private seen = new Map<number, number>();
    private floats: {
        text: Text;
        at: number;
        worldX: number;
        worldY: number;
    }[] = [];
    private confetti: { dot: Graphics; worldX: number; worldY: number; x: number; y: number; vx: number; vy: number; at: number }[] = [];
    private mission = -1;
    private flightCount = -1;
    /** Last seen counter cash and open state per station, so each sale and each opening gets its own pop. */
    private pending = new Map<number, number>();
    private opened = new Map<number, boolean>();
    private speed = new Map<number, number>();
    private walkoutSeen = -1;
    /** Bill flights (doc 10 §4.3/§4.4): a `captain` target is the cash vacuum, a fixed `to` the build pour. */
    private flights: { gfx: Graphics; from: { x: number; y: number }; to: { x: number; y: number }; captain: number; at: number; dur: number }[] = [];
    /** Last seen invested amount per stage, so pour bills only fly while money actually flows. */
    private pourInvested = new Map<number, number>();
    private root: unknown = null;
    run = (g: ReadOnlyGameModel) => {
        const scene = g.getSystem(AirportScene), root = scene.world;
        if (!root)
            return;
        // A rebuilt scene (another airport's floor plan) destroyed the old world and every effect drawn in it.
        if (root !== this.root) {
            this.root = root;
            this.floats = this.floats.filter(f => !f.text.destroyed);
            this.confetti = this.confetti.filter(f => !f.dot.destroyed);
            this.flights = this.flights.filter(f => !f.gfx.destroyed);
        }
        const e = state(g);
        // A short confetti burst where the captain stands when an objective completes.
        if (e.mission !== this.mission) {
            const first = this.mission < 0;
            this.mission = e.mission;
            const captain = ids(g, AirportAgent)[0];
            if (!first && captain !== undefined && e.mission > 0) {
                const p = g.getTypedUnsafe(Transform, captain);
                this.burst(root, p.x, p.y, g.frame);
            }
            // A paid lesson rains its bills onto the captain, so the reward is seen, not just read.
            const reward = !first && e.mission > 0 ? missionsFor(e)[e.mission - 1]?.reward ?? 0 : 0;
            if (reward > 0 && captain !== undefined) {
                const p = g.getTypedUnsafe(Transform, captain), origin = projectWorld(p.x, p.y);
                const text = label(root, `+${reward}`, origin.x, origin.y - 120, 24, C.gold, '900');
                text.anchor.set(0.5);
                Object.assign(text.style, { stroke: 0xffffff, strokeThickness: 4.5, lineJoin: 'round' });
                text.zIndex = 100000;
                this.floats.push({ text, at: g.frame, worldX: p.x, worldY: p.y - 40 });
                if (!LOCAL.reducedMotion)
                    for (let i = 0; i < Math.min(16, 4 + reward / 10) && this.flights.length < 48; i++) {
                        const [jx, jy] = VACUUM_SPOTS[i % VACUUM_SPOTS.length];
                        this.flights.push({ gfx: billSprite(root), from: { x: p.x + jx * 9, y: p.y + jy * 9 - 160 }, to: { x: p.x, y: p.y }, captain, at: g.frame + i * 2, dur: 22 });
                    }
            }
        }
        this.confetti = this.confetti.filter(f => { const age = g.frame - f.at; if (age > 55 || this.confetti.length > 60) {
            f.dot.destroy();
            return false;
        } f.x += f.vx; f.y += f.vy; const origin = projectWorld(f.worldX, f.worldY); f.dot.position.set(origin.x + f.x, origin.y + f.y); f.vy += 0.34; f.dot.rotation += 0.2; f.dot.alpha = Math.min(1, (55 - age) / 18); return true; });
        // One red loss floater where a passenger gave up waiting.
        if (e.walkoutUntil > g.frame && this.walkoutSeen !== e.walkoutUntil) {
            this.walkoutSeen = e.walkoutUntil;
            if (e.walkoutAmount > 0) {
                const origin = projectWorld(e.walkoutX, e.walkoutY);
                const text = label(root, `-${e.walkoutAmount}`, origin.x, origin.y - 90, 15, 0xb4534f, '800');
                text.anchor.set(0.5);
                text.zIndex = 100000;
                this.floats.push({ text, at: g.frame, worldX: e.walkoutX, worldY: e.walkoutY });
            }
        }
        // Every departure gets its own moment over the gate it left from.
        if (this.flightCount >= 0 && e.flights > this.flightCount) {
            const plane = ids(g, AirportPlane).map(id => ({ id, p: g.getTypedUnsafe(AirportPlane, id) })).sort((a, b) => a.p.clock - b.p.clock).find(({ p }) => p.phase === 1);
            const at = plane ? g.getTypedUnsafe(Transform, plane.id) : null;
            if (at) {
                const origin = projectWorld(at.x, at.y);
                const text = label(root, `✈ Flight ${e.flights}!`, origin.x, origin.y - 90, 20, C.teal, '900');
                text.anchor.set(0.5);
                Object.assign(text.style, { stroke: 0xffffff, strokeThickness: 4, lineJoin: 'round' });
                text.zIndex = 100000;
                this.floats.push({ text, at: g.frame, worldX: at.x, worldY: at.y });
            }
        }
        this.flightCount = e.flights;
        for (const st of stations(g)) {
            // Each sale pops its takings over the counter pile before anyone collects them.
            const before = this.pending.get(st.index);
            if (before !== undefined && st.pending > before) this.pop(root, `+${st.pending - before}`, cashPoint(st.index), g.frame, 14, C.gold, -40);
            this.pending.set(st.index, st.pending);
            // A department that just opened gets a burst and its name on the floor.
            const was = this.opened.get(st.index);
            if (was === false && st.unlocked) {
                const at = stationPad(st.index, 'work');
                this.burst(root, at.x, at.y, g.frame);
                this.pop(root, `${CONFIG.stages[st.index].name} OPEN!`, at, g.frame, 22, C.teal, -30);
            }
            this.opened.set(st.index, st.unlocked);
            const faster = this.speed.get(st.index);
            if (faster !== undefined && st.speedLevel > faster) {
                const at = stationPad(st.index, 'work');
                this.burst(root, at.x, at.y, g.frame);
                this.pop(root, '⚡ Faster!', at, g.frame, 20, C.teal, -30);
            }
            this.speed.set(st.index, st.speedLevel);
        }
        this.pourBills(g, root);
        for (const id of ids(g, AirportAgent)) {
            const a = g.getTypedUnsafe(AirportAgent, id);
            if (a.feedbackUntil <= g.frame || this.seen.get(id) === a.feedbackUntil)
                continue;
            this.seen.set(id, a.feedbackUntil);
            // Collections become a gold `+N`; other allowed feedback keeps its sentence floater.
            const gold = collectedAmount(a.feedback);
            if (gold === null && !a.feedback.startsWith('+') && !a.feedback.startsWith('Loaded') && !a.feedback.includes('Level') && !a.feedback.includes('upgrade complete') && !a.feedback.startsWith('Improved.'))
                continue;
            const p = g.getTypedUnsafe(Transform, id), origin = projectWorld(p.x, p.y);
            const text = label(root, gold !== null ? `+${gold}` : a.feedback, origin.x, origin.y - 90, gold !== null ? 17 : 15, gold !== null ? C.gold : C.navy, '800');
            text.anchor.set(0.5);
            if (gold !== null)
                Object.assign(text.style, { stroke: 0xffffff, strokeThickness: 3.5, lineJoin: 'round' });
            text.zIndex = 100000;
            this.floats.push({ text, at: g.frame, worldX: p.x, worldY: p.y });
            if (gold !== null && !LOCAL.reducedMotion)
                this.vacuum(g, root, id, p, gold);
        }
        this.flights = this.flights.filter(f => {
            const age = g.frame - f.at;
            if (age > f.dur) {
                f.gfx.destroy();
                return false;
            }
            if (age < 0) {
                f.gfx.visible = false;
                return true;
            }
            f.gfx.visible = true;
            // A vacuum bill chases its captain's live position; a pour bill flies to a fixed pad.
            const to = f.captain >= 0 && g.hasComponent(Transform, f.captain) ? g.getTypedUnsafe(Transform, f.captain) : f.to;
            const p = age / f.dur;
            const x = f.from.x + (to.x - f.from.x) * p, y = f.from.y + (to.y - f.from.y) * p - Math.sin(p * Math.PI) * 26;
            const origin = projectWorld(x, y);
            f.gfx.position.set(origin.x, origin.y);
            f.gfx.rotation = p * 2.5;
            f.gfx.alpha = p > 0.85 ? (1 - p) / 0.15 : 1;
            return true;
        });
        this.floats = this.floats.filter(f => { const age = g.frame - f.at; if (age > 85 || this.floats.length > 24) {
            f.text.destroy();
            return false;
        } const origin = projectWorld(f.worldX, f.worldY); f.text.position.set(origin.x, origin.y - 90 - (LOCAL.reducedMotion ? 0 : age * 0.45)); f.text.alpha = Math.min(1, (85 - age) / 20); return true; });
    };
    /** A short outlined floater that rises from a world point. */
    private pop(root: Container, text: string, at: { x: number; y: number }, frame: number, size: number, color: number, lift: number) {
        const origin = projectWorld(at.x, at.y);
        const node = label(root, text, origin.x, origin.y - 90, size, color, '900');
        node.anchor.set(0.5);
        Object.assign(node.style, { stroke: 0xffffff, strokeThickness: size > 16 ? 4 : 3, lineJoin: 'round' });
        node.zIndex = 100000;
        this.floats.push({ text: node, at: frame, worldX: at.x, worldY: at.y + lift });
    }
    /** Confetti from a world point (lesson complete, a department opening). */
    private burst(root: Container, x: number, y: number, frame: number) {
        if (LOCAL.reducedMotion) return;
        for (let i = 0; i < 14; i++) {
            const dot = new Graphics();
            dot.beginFill(CONFETTI[i % CONFETTI.length]).drawRect(-4, -6, 8, 12).endFill();
            const origin = projectWorld(x, y);
            dot.position.set(origin.x, origin.y - 60);
            dot.zIndex = 100001;
            root.addChild(dot);
            const angle = i * 2.399963; // golden angle: even spread, deterministic
            this.confetti.push({ dot, worldX: x, worldY: y, x: 0, y: -60, vx: Math.cos(angle) * 3.2, vy: Math.sin(angle) * 3.2 - 3.4, at: frame });
        }
    }
    /** Bills arc from the collected pile to the captain (presentation only; the sim already banked them). */
    private vacuum(g: ReadOnlyGameModel, root: Container, captain: number, at: { x: number; y: number }, amount: number) {
        let pile: { x: number; y: number } | null = null, best = Infinity;
        for (const id of ids(g, AirportCash)) {
            const p = g.getTypedUnsafe(Transform, id), d = (p.x - at.x) ** 2 + (p.y - at.y) ** 2;
            if (d < best) {
                best = d;
                pile = p;
            }
        }
        if (!pile)
            return;
        const count = Math.min(12, Math.max(4, Math.round(amount / 20)));
        for (let i = 0; i < count && this.flights.length < 48; i++) {
            const jitter = VACUUM_SPOTS[i % VACUUM_SPOTS.length];
            this.flights.push({ gfx: billSprite(root), from: { x: pile.x + jitter[0], y: pile.y + jitter[1] }, to: { x: at.x, y: at.y }, captain, at: g.frame + i * 2, dur: 16 });
        }
    }
    /** While a captain pours into a build pad, bills stream from them onto the pad. */
    private pourBills(g: ReadOnlyGameModel, root: Container) {
        if (LOCAL.reducedMotion)
            return;
        for (const s of stations(g)) {
            const before = this.pourInvested.get(s.index) ?? s.invested;
            this.pourInvested.set(s.index, s.invested);
            if (s.invested <= before || s.invested === 0 || g.frame % 2 !== 0 || this.flights.length >= 48)
                continue;
            const builder = ids(g, AirportAgent).find(id => { const a = g.getTypedUnsafe(AirportAgent, id); return a.building === s.index && a.dwell > 30; });
            if (builder === undefined)
                continue;
            const p = g.getTypedUnsafe(Transform, builder), pad = stationPad(s.index, 'build');
            this.flights.push({ gfx: billSprite(root), from: { x: p.x, y: p.y - 30 }, to: { x: pad.x, y: pad.y }, captain: -1, at: g.frame, dur: 12 });
        }
    }
    cleanup = () => { for (const f of this.floats)
        if (!f.text.destroyed)
            f.text.destroy(); this.floats = []; this.seen.clear(); for (const f of this.confetti)
        if (!f.dot.destroyed)
            f.dot.destroy(); this.confetti = []; for (const f of this.flights)
        if (!f.gfx.destroyed)
            f.gfx.destroy(); this.flights = []; this.pourInvested.clear(); this.flightCount = -1; this.pending.clear(); this.opened.clear(); this.speed.clear(); };
}
/** Deterministic pile entry points for the vacuum, so no randomness is needed. */
const VACUUM_SPOTS: [number, number][] = [[0, 0], [7, 3], [-6, 5], [4, -6], [-8, -3], [6, 8], [-3, -9], [9, -2], [-9, 6], [2, 9], [8, 7], [-5, -9]];
function billSprite(root: Container): Graphics {
    const g = new Graphics();
    g.beginFill(C.gold).drawRoundedRect(-5, -3, 10, 6, 2).endFill();
    g.beginFill(0xfff3c9).drawRect(-5, -1, 10, 2).endFill();
    g.zIndex = 100002;
    root.addChild(g);
    return g;
}
